"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGetProductAttributesValuesForStrategyMatrix = void 0;
const react_1 = require("react");
const react_2 = require("@apollo/client/react");
const gql_1 = require("../gql");
const client_constants_1 = require("../../../client/client.constants");
const client_utils_1 = require("../../../client/client.utils");
const use_offset_cursor_parallel_pagination_hook_1 = require("../../../common/table-pagination/use-offset-cursor-parallel-pagination.hook");
const updateProductAggregatedAttributeValuesQuery = (prev, { fetchMoreResult }) => {
    var _a;
    if (!((_a = fetchMoreResult === null || fetchMoreResult === void 0 ? void 0 : fetchMoreResult.catalogs) === null || _a === void 0 ? void 0 : _a.edges))
        return prev;
    return Object.assign(Object.assign({}, fetchMoreResult), { catalogs: Object.assign(Object.assign({}, fetchMoreResult.catalogs), { edges: prev.catalogs.edges.map((prevCat, idx) => {
                var _a, _b;
                const newCat = fetchMoreResult.catalogs.edges[idx];
                if (!newCat)
                    return prevCat;
                const prevAttr = (_a = prevCat.node.statistics.attributes.edges) !== null && _a !== void 0 ? _a : [];
                const newAttr = (_b = newCat.node.statistics.attributes.edges) !== null && _b !== void 0 ? _b : [];
                return Object.assign(Object.assign({}, newCat), { node: Object.assign(Object.assign({}, newCat.node), { statistics: Object.assign(Object.assign({}, newCat.node.statistics), { attributes: Object.assign(Object.assign({}, newCat.node.statistics.attributes), { edges: [...prevAttr, ...newAttr] }) }) }) });
            }) }) });
};
const mergeProductAggregatedAttributeValuesQuery = (initialData, fetchMoreResults) => {
    const [firstResult, ...remainingResults] = fetchMoreResults;
    const seed = initialData !== null && initialData !== void 0 ? initialData : firstResult;
    if (!seed)
        throw new Error('No product aggregated attribute values data to merge.');
    return remainingResults.reduce((acc, fetchMoreResult) => updateProductAggregatedAttributeValuesQuery(acc, { fetchMoreResult }), seed);
};
const useGetProductAttributesValuesForStrategyMatrix = ({ ids, attribute, productTreeId, pageSize = client_constants_1.REQUEST_LIMIT_MAX, productTreeNodeIds, }) => {
    var _a, _b;
    const baseVariables = (0, react_1.useMemo)(() => ({
        ids,
        attribute,
        productTreeId,
        productTreeNodeIds,
    }), [ids, attribute, productTreeId, productTreeNodeIds]);
    const skip = !(ids === null || ids === void 0 ? void 0 : ids.length)
        || !attribute
        || !productTreeId
        || !(productTreeNodeIds === null || productTreeNodeIds === void 0 ? void 0 : productTreeNodeIds.length);
    const { data, error, networkStatus, } = (0, react_2.useQuery)(gql_1.GET_PRODUCT_AGGREGATED_ATTRIBUTE_VALUES_PAGINATION, {
        notifyOnNetworkStatusChange: true,
        variables: baseVariables,
        skip,
    });
    const attributes = (_a = data === null || data === void 0 ? void 0 : data.catalogs.edges[0]) === null || _a === void 0 ? void 0 : _a.node.statistics.attributes;
    const requestKey = (0, react_1.useMemo)(() => JSON.stringify({
        baseVariables,
        pageSize,
        totalCount: attributes === null || attributes === void 0 ? void 0 : attributes.totalCount,
    }), [attributes === null || attributes === void 0 ? void 0 : attributes.totalCount, baseVariables, pageSize]);
    const mergeResults = (0, react_1.useCallback)((initialData, fetchMoreResults) => mergeProductAggregatedAttributeValuesQuery(initialData, fetchMoreResults), []);
    const { data: aggregatedData, isLoading: isLoadingRemainingPages, error: remainingPagesError, } = (0, use_offset_cursor_parallel_pagination_hook_1.useOffsetCursorParallelPagination)({
        query: gql_1.GET_PRODUCT_AGGREGATED_ATTRIBUTE_DATA,
        initialData: undefined,
        baseVariables,
        pageSize,
        totalCount: (_b = attributes === null || attributes === void 0 ? void 0 : attributes.totalCount) !== null && _b !== void 0 ? _b : 0,
        hasNextPage: attributes === null || attributes === void 0 ? void 0 : attributes.pageInfo.hasNextPage,
        mergeResults,
        includeInitialPage: true,
        requestKey,
        skip: skip || !attributes,
    });
    const terminalNodesData = (0, react_1.useMemo)(() => {
        var _a;
        if (!((_a = aggregatedData === null || aggregatedData === void 0 ? void 0 : aggregatedData.catalogs) === null || _a === void 0 ? void 0 : _a.edges))
            return [];
        return aggregatedData.catalogs.edges.flatMap((catalog) => {
            var _a, _b, _c, _d;
            const statistics = (_d = (_c = (_b = (_a = catalog === null || catalog === void 0 ? void 0 : catalog.node) === null || _a === void 0 ? void 0 : _a.statistics) === null || _b === void 0 ? void 0 : _b.attributes) === null || _c === void 0 ? void 0 : _c.edges) !== null && _d !== void 0 ? _d : [];
            return statistics.map((stat) => {
                const { node } = stat;
                const { dimensions } = node;
                const terminalNode = dimensions.productTreeNode;
                return {
                    terminalNodeId: terminalNode === null || terminalNode === void 0 ? void 0 : terminalNode.id,
                    terminalNodeTitle: terminalNode === null || terminalNode === void 0 ? void 0 : terminalNode.title,
                    attribute: dimensions === null || dimensions === void 0 ? void 0 : dimensions.attribute,
                    value: dimensions === null || dimensions === void 0 ? void 0 : dimensions.value,
                    applied: node === null || node === void 0 ? void 0 : node.applied,
                };
            });
        });
    }, [aggregatedData]);
    const totalProductsCountData = (0, react_1.useMemo)(() => {
        var _a;
        if (!((_a = aggregatedData === null || aggregatedData === void 0 ? void 0 : aggregatedData.catalogs) === null || _a === void 0 ? void 0 : _a.edges))
            return [];
        return aggregatedData.catalogs.edges.flatMap((catalog) => { var _a, _b; return (_b = (_a = catalog.node.statistics.groupedTotals) === null || _a === void 0 ? void 0 : _a.groupedTotals) !== null && _b !== void 0 ? _b : []; });
    }, [aggregatedData]);
    return {
        data: {
            terminalNodesData,
            totalProductsCount: totalProductsCountData,
        },
        isLoading: (0, client_utils_1.isQueryLoadingOrRefetching)(networkStatus) || isLoadingRemainingPages,
        error: (error === null || error === void 0 ? void 0 : error.message) || remainingPagesError,
    };
};
exports.useGetProductAttributesValuesForStrategyMatrix = useGetProductAttributesValuesForStrategyMatrix;
